import React, { useState } from 'react';
import { CheckSquare, ChevronRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import type { TaskItem } from './types';

interface MyTasksWidgetProps {
  tasks: TaskItem[];
  onToggleTask: (taskId: string) => void;
  onNewTask: () => void;
}

export const MyTasksWidget: React.FC<MyTasksWidgetProps> = ({ tasks, onToggleTask, onNewTask }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'today' | 'overdue' | 'upcoming'>('today');

  const filtered = tasks.filter((t) => {
    if (tab === 'today') return t.dueCategory === 'today';
    if (tab === 'overdue') return t.dueCategory === 'overdue';
    return t.dueCategory === 'upcoming';
  });

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-danger/5 text-danger-text border-danger/30';
      case 'medium':
        return 'bg-warning/10 text-warning-text border-warning/30';
      default:
        return 'bg-surface-variant text-on-surface-variant border-border';
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-warning/10 text-warning-text">
              <CheckSquare className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                My Tasks ({tasks.filter((t) => !t.isCompleted).length})
              </span>
              <div className="text-data-xs text-on-surface-subtle">Daily action items & checklist</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onNewTask}
              className="inline-flex items-center gap-1 rounded-lg border border-warning/30 bg-warning/5 px-2.5 py-1 text-body-sm font-semibold text-warning-text hover:bg-warning/10 active:scale-95 transition"
            >
              <Plus className="h-3 w-3" />
              <span>Add Task</span>
            </button>
            <button
              type="button"
              onClick={() => navigate(ROUTES.tasks)}
              className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
            >
              <span>View All</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Tab Strip */}
        <div className="mt-3 flex items-center gap-1 border-b border-border pb-2">
          <button
            type="button"
            onClick={() => setTab('today')}
            className={`rounded-md px-2.5 py-1 text-body-sm font-semibold transition ${
              tab === 'today' ? 'bg-warning/10 text-warning-text' : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Today ({tasks.filter((t) => t.dueCategory === 'today').length})
          </button>
          <button
            type="button"
            onClick={() => setTab('upcoming')}
            className={`rounded-md px-2.5 py-1 text-body-sm font-semibold transition ${
              tab === 'upcoming' ? 'bg-surface-container text-on-surface' : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Upcoming ({tasks.filter((t) => t.dueCategory === 'upcoming').length})
          </button>
        </div>

        {/* Tasks List */}
        <div className="my-2 flex flex-col divide-y divide-border">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-body-sm text-on-surface-variant">
              No tasks pending for this section.
            </div>
          ) : (
            filtered.map((task) => (
              <div
                key={task.id}
                className="flex items-center justify-between py-2.5 transition hover:bg-surface-variant px-2 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={task.isCompleted}
                    onChange={() => onToggleTask(task.id)}
                    className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary cursor-pointer"
                  />
                  <div>
                    <span
                      className={`text-body-sm font-semibold text-on-surface ${
                        task.isCompleted ? 'line-through text-on-surface-subtle' : ''
                      }`}
                    >
                      {task.title}
                    </span>
                    <div className="flex items-center gap-2 text-data-xs text-on-surface-subtle mt-0.5">
                      <span>{task.customer}</span>
                      <span>•</span>
                      <span>Due: {task.dueDate}</span>
                    </div>
                  </div>
                </div>

                <span
                  className={`rounded-md border px-2 py-0.5 text-data-xs font-semibold uppercase ${getPriorityBadge(
                    task.priority
                  )}`}
                >
                  {task.priority}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Completed today:</span>
        <span className="font-semibold text-success-text">
          {tasks.filter((t) => t.isCompleted).length} / {tasks.length} tasks finished
        </span>
      </div>
    </div>
  );
};
